import { useState, useMemo, useEffect } from 'react';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import getTheme from './styles/theme';
import './styles/globals.css';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './components/ui/ToastProvider';
import RouteTitle from './components/RouteTitle';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Cryopreservation from './pages/Cryopreservation';
import DashboardPage from './pages/DashboardPage';
import AuditLogPage from './pages/AuditLogPage';
import UsersPage from './pages/UsersPage';

export default function App() {
  // Light is the default theme; respect a previously saved preference.
  const [mode, setMode] = useState(() => {
    try {
      // One-time reset to the warm light theme (biodimension.in default).
      // Users can still toggle dark afterwards; the flag prevents repeats.
      if (!localStorage.getItem('theme-light-reset-v1')) {
        localStorage.setItem('theme-light-reset-v1', '1');
        localStorage.setItem('theme-mode', 'light');
        return 'light';
      }
      const saved = localStorage.getItem('theme-mode');
      return saved === 'dark' || saved === 'light' ? saved : 'light';
    } catch {
      return 'light';
    }
  });

  const theme = useMemo(() => {
    const themeConfig = getTheme(mode);
    return createTheme(themeConfig as any);
  }, [mode]);

  const handleToggleTheme = () => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  // Sync Tailwind dark class on <html> and persist the preference when mode changes.
  // This is a side effect (DOM mutation + storage), so it belongs in useEffect,
  // not useMemo (which is for computing values and should stay side-effect free).
  useEffect(() => {
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    try {
      localStorage.setItem('theme-mode', mode);
    } catch {
      /* ignore persistence errors (e.g. private mode) */
    }
  }, [mode]);

  // Biodimension Design System — global MUI overrides.
  // The document is the single scroll container: the browser shows a vertical
  // scrollbar only when content overflows the viewport. We intentionally do NOT
  // force `overflow-y: scroll` or reserve a `scrollbar-gutter`, so there is no
  // permanent empty band on the right and the fixed top nav spans the full width.
  const globalStyles = {
    'html, body': {
      margin: 0,
      padding: 0,
    },
    '#root': {
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={globalStyles} />
      <ErrorBoundary>
        <ToastProvider>
        <Router>
          <RouteTitle />
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <Layout mode={mode} toggleTheme={handleToggleTheme}>
                      <Cryopreservation />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout mode={mode} toggleTheme={handleToggleTheme}>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/audit"
                element={
                  <ProtectedRoute>
                    <Layout mode={mode} toggleTheme={handleToggleTheme}>
                      <AuditLogPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/users"
                element={
                  <ProtectedRoute>
                    <Layout mode={mode} toggleTheme={handleToggleTheme}>
                      <UsersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/app" />} />
            </Routes>
          </AuthProvider>
        </Router>
        </ToastProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}